/**
 * Shared save data types for daily puzzle games
 * Provides type safety and consistency across all games
 */

/**
 * LevelScore type for game progress tracking
 * Used across multiple games to track puzzle completion
 */
export interface LevelScore<TGameMetadata = any> {
  /** Unique identifier for the game */
  gameId?: string;
  /** Unique identifier for the level/puzzle */
  levelId: string;
  /** Whether the player won/completed the level */
  isWon: boolean;
  /** Custom game-specific metadata */
  gameMetadata?: TGameMetadata;
  /** Timestamp of when this score was recorded */
  timestamp?: number;
  /** Total number of attempts */
  numTries?: number;
  /** Number of tries it took to win (only set if isWon is true) */
  numTriesTillWon?: number;
  /** Time taken on the original/first successful completion */
  originalTimeSec?: number;
  /** Best (fastest) completion time */
  bestTimeSec?: number;
  /** Difficulty level of the puzzle */
  difficulty?: string;
  /** Whether a hint was used when winning */
  usedHintTillWon?: boolean;
}

export interface Player {
  id: string;
  name: string;
}

/**
 * Base save data structure for daily puzzle games
 * Extend this interface for game-specific data
 * 
 * @template TLevelScoreData - Optional custom data stored in each LevelScore
 * 
 * @example
 * ```typescript
 * interface MyGameSaveData extends DailyPuzzleGameSaveData {
 *   myCustomField: string;
 * }
 * ```
 */
export interface DailyPuzzleGameSaveData<TLevelScoreData = any> {
  /** Version of the save data format (increment on breaking changes) */
  version: number;
  
  /** 
   * Map of levelId -> LevelScore for all completed/attempted puzzles
   * Using Record for O(1) lookups by level ID
   */
  levelScores: Record<string, LevelScore<TLevelScoreData>>;
  

  /** Current streak (consecutive daily completions) */
  currentStreak?: number;
  
  /** Best streak (highest consecutive daily completions ever achieved) */
  bestStreak?: number;
  
  /** Last date the player completed a daily puzzle (YYYY-MM-DD format) */
  lastCompletionDate?: string;
}

/**
 * Type guard to check if save data has the daily puzzle structure
 */
export function isDailyPuzzleGameSaveData(data: any): data is DailyPuzzleGameSaveData {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.version === 'number' &&
    typeof data.levelScores === 'object' &&
    !Array.isArray(data.levelScores) // Must be a Record, not an Array
  );
}

/**
 * Helper to get a level score from save data with type safety
 */
export function getLevelScoreFromSaveData<TData extends DailyPuzzleGameSaveData>(
  saveData: TData | null | undefined,
  levelId: string
): LevelScore<any> | undefined {
  if (!saveData?.levelScores) {
    return undefined;
  }
  
  return saveData.levelScores[levelId];
}

/**
 * Helper to get all level scores as an array (useful for iteration)
 */
export function getAllLevelScores<TData extends DailyPuzzleGameSaveData>(
  saveData: TData | null | undefined
): LevelScore<any>[] {
  if (!saveData?.levelScores) {
    return [];
  }
  
  return Object.values(saveData.levelScores);
}
